import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, X,
  Key, UploadSimple, DownloadSimple, Export,
} from "@phosphor-icons/react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CATEGORIES, PROCESSES, CATEGORY_COLORS } from "@/lib/meta";

const empty = {
  name: "", category: "Metal", composition: "", recommended_processes: [],
  hardness: "", porosity: "", bond_strength: "", density: "", melting_point: "",
  spray_parameters: "", microstructure: "", applications: "", notes: "",
};

export default function Admin() {
  const { user, loading } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const fileRef = useRef(null);
  const microRef = useRef(null);
  const [microVer, setMicroVer] = useState(0);
  const [microBusy, setMicroBusy] = useState(false);
  const [formMicroPath, setFormMicroPath] = useState("");

  const load = async () => {
    const res = await api.get("/materials");
    setMaterials(res.data);
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (loading) {
    return <div className="grain min-h-[60vh] flex items-center justify-center font-mono text-sm text-muted-foreground">checking session<span className="cursor-blink">_</span></div>;
  }
  if (!user) return null;

  const openCreate = () => { setForm(empty); setEditId(null); setFormMicroPath(""); setOpen(true); };
  const openEdit = (m) => {
    setForm({ ...empty, ...m });
    setEditId(m.id);
    setFormMicroPath(m.micrograph_path || "");
    setMicroVer((v) => v + 1);
    setOpen(true);
  };

  const uploadMicrograph = async () => {
    const f = microRef.current?.files?.[0];
    if (!f || !editId) return;
    setMicroBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post(`/materials/${editId}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFormMicroPath(res.data.micrograph_path || "");
      setMicroVer((v) => v + 1);
      if (microRef.current) microRef.current.value = "";
      toast.success("Micrograph uploaded");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setMicroBusy(false); }
  };

  const removeMicrograph = async () => {
    if (!editId) return;
    try {
      await api.delete(`/materials/${editId}/image`);
      setFormMicroPath("");
      setMicroVer((v) => v + 1);
      toast.success("Micrograph removed");
      await load();
    } catch {
      toast.error("Remove failed");
    }
  };

  const toggleProcess = (p) => {
    setForm((f) => ({
      ...f,
      recommended_processes: f.recommended_processes.includes(p)
        ? f.recommended_processes.filter((x) => x !== p)
        : [...f.recommended_processes, p],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.created_at;
      if (editId) {
        await api.put(`/materials/${editId}`, payload);
        toast.success("Material updated");
      } else {
        await api.post("/materials", payload);
        toast.success("Material added");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/materials/${deleteTarget.id}`);
      toast.success("Material removed");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  // Change password
  const changePwd = async (e) => {
    e.preventDefault();
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: curPwd, new_password: newPwd });
      toast.success("Password updated");
      setPwdOpen(false);
      setCurPwd(""); setNewPwd("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setPwdBusy(false);
    }
  };

  // Bulk import
  const doImport = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choose a CSV file"); return; }
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/materials/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(res.data);
      toast.success(`Imported: ${res.data.created} new, ${res.data.updated} updated`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadTemplate = () => {
    const header = "name,category,composition,recommended_processes,hardness,porosity,bond_strength,density,melting_point,spray_parameters,microstructure,applications,notes";
    const sample = 'Example Alloy,Metal,Ni-20Cr,HVOF;Plasma Spray,320 HV,2%,55 MPa,8.4 g/cm3,1400 C,HVOF dense low-oxide,Dense lamellar splats with fine oxide stringers,High-temp oxidation resistance,Separate processes with semicolons';
    const blob = new Blob([header + "\n" + sample + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "materials_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/materials/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "thermal_spray_materials.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Database exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const inputCls = "rounded-sm bg-background border-border font-mono text-sm focus-visible:ring-primary";

  return (
    <div className="grain mx-auto max-w-[1400px] px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-heading font-black text-white text-2xl">Admin — Database Manager</h1>
          <p className="text-sm text-muted-foreground">
            Signed in as <span className="text-secondary font-mono">{user.email}</span>
            {user.is_admin && <span className="ml-2 text-primary font-mono text-xs">[ADMIN]</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="change-password-btn" onClick={() => setPwdOpen(true)} variant="outline" className="rounded-sm border-border bg-card hover:bg-muted text-muted-foreground hover:text-white font-mono uppercase text-xs tracking-widest">
            <Key size={16} weight="bold" className="mr-1.5" /> Password
          </Button>
          {user.is_admin && (
            <Button data-testid="import-csv-btn" onClick={() => { setBulkResult(null); setBulkOpen(true); }} variant="outline" className="rounded-sm border-border bg-card hover:bg-muted text-muted-foreground hover:text-white font-mono uppercase text-xs tracking-widest">
              <UploadSimple size={16} weight="bold" className="mr-1.5" /> Import CSV
            </Button>
          )}
          {user.is_admin && (
            <Button data-testid="export-csv-btn" onClick={exportCsv} variant="outline" className="rounded-sm border-border bg-card hover:bg-muted text-muted-foreground hover:text-white font-mono uppercase text-xs tracking-widest">
              <Export size={16} weight="bold" className="mr-1.5" /> Export CSV
            </Button>
          )}
          <Button data-testid="add-material-btn" onClick={openCreate} disabled={!user.is_admin} className="rounded-sm bg-primary hover:bg-primary/90 text-white font-mono uppercase text-xs tracking-widest">
            <Plus size={16} weight="bold" className="mr-1.5" /> Add Material
          </Button>
        </div>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="max-w-sm rounded-sm bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">Change Password</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">Update the password for your account.</DialogDescription>
          </DialogHeader>
          <form onSubmit={changePwd} className="space-y-3 py-2">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Current Password</label>
              <Input data-testid="cur-password" type="password" value={curPwd} onChange={(e) => setCurPwd(e.target.value)} className={`mt-1.5 ${inputCls}`} />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">New Password (min 8 chars)</label>
              <Input data-testid="new-password" type="password" required minLength={8} value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className={`mt-1.5 ${inputCls}`} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setPwdOpen(false)} className="rounded-sm">Cancel</Button>
              <Button data-testid="save-password-btn" type="submit" disabled={pwdBusy} className="rounded-sm bg-primary hover:bg-primary/90 text-white font-mono uppercase text-xs tracking-widest">
                {pwdBusy ? "Saving..." : "Update"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg rounded-sm bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">Import Materials (CSV)</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              Upload a CSV to add or update many materials at once. Existing names are updated; new names are added.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <button onClick={downloadTemplate} data-testid="download-template-btn" className="flex items-center gap-2 text-xs font-mono text-secondary hover:text-secondary/80">
              <DownloadSimple size={15} /> Download CSV template
            </button>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">CSV File</label>
              <input
                ref={fileRef}
                data-testid="csv-file-input"
                type="file"
                accept=".csv,text/csv"
                className="mt-1.5 block w-full text-sm text-muted-foreground font-mono file:mr-3 file:rounded-sm file:border-0 file:bg-primary file:px-3 file:py-2 file:text-white file:font-mono file:text-xs file:uppercase file:cursor-pointer bg-background border border-border rounded-sm p-1"
              />
            </div>
            <p className="font-mono text-[10px] text-muted-foreground leading-relaxed">
              Columns: name, category, composition, recommended_processes (use ; between processes), hardness, porosity, bond_strength, density, melting_point, spray_parameters, applications, notes.
            </p>
            {bulkResult && (
              <div data-testid="bulk-result" className="border border-border bg-background p-3 rounded-sm text-sm">
                <span className="text-emerald-400 font-mono">{bulkResult.created} created</span>
                <span className="text-muted-foreground mx-2">·</span>
                <span className="text-secondary font-mono">{bulkResult.updated} updated</span>
                {bulkResult.skipped > 0 && (
                  <>
                    <span className="text-muted-foreground mx-2">·</span>
                    <span className="text-amber-400 font-mono">{bulkResult.skipped} skipped (blank/invalid)</span>
                  </>
                )}
                {bulkResult.errors?.length > 0 && (
                  <div className="mt-2 text-xs text-destructive">
                    {bulkResult.errors.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setBulkOpen(false)} className="rounded-sm">Close</Button>
            <Button data-testid="run-import-btn" onClick={doImport} disabled={bulkBusy} className="rounded-sm bg-primary hover:bg-primary/90 text-white font-mono uppercase text-xs tracking-widest">
              {bulkBusy ? "Importing..." : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!user.is_admin && (
        <div className="border border-amber-400/40 bg-amber-400/10 p-3 mb-5 text-sm text-amber-300">
          Your account is authenticated but does not have admin rights. Editing is restricted.
        </div>
      )}

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted">
              {["Material", "Category", "Processes", "Hardness", "Porosity", "Bond", ""].map((h) => (
                <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-3 py-2.5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {materials.map((m) => (
              <tr key={m.id} data-testid={`admin-row-${m.id}`} className="border-b border-border/60 hover:bg-muted/40 transition-colors">
                <td className="px-3 py-2.5">
                  <div className="text-white font-medium">{m.name}</div>
                  {m.composition && <div className="font-mono text-xs text-muted-foreground">{m.composition}</div>}
                </td>
                <td className="px-3 py-2.5">
                  <span className={`border px-2 py-0.5 text-[10px] font-mono uppercase rounded-sm ${CATEGORY_COLORS[m.category] || CATEGORY_COLORS.Other}`}>{m.category}</span>
                </td>
                <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground max-w-[200px]">{m.recommended_processes?.join(", ")}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{m.hardness}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{m.porosity}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{m.bond_strength}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1 justify-end">
                    <Button data-testid={`edit-btn-${m.id}`} onClick={() => openEdit(m)} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-muted-foreground hover:text-secondary" disabled={!user.is_admin}>
                      <PencilSimple size={16} />
                    </Button>
                    <Button data-testid={`delete-btn-${m.id}`} onClick={() => setDeleteTarget(m)} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-muted-foreground hover:text-destructive" disabled={!user.is_admin}>
                      <Trash size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm bg-card border-border max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">{editId ? "Edit Material" : "Add Material"}</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">Enter the feedstock details and coating properties.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Name *</label>
              <Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`mt-1.5 ${inputCls}`} />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="form-category" className={`mt-1.5 ${inputCls}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="font-mono text-sm">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Composition</label>
              <Input data-testid="form-composition" value={form.composition} onChange={(e) => setForm({ ...form, composition: e.target.value })} className={`mt-1.5 ${inputCls}`} />
            </div>
            <div className="sm:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Recommended Processes</label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {PROCESSES.map((p) => {
                  const on = form.recommended_processes.includes(p);
                  return (
                    <button
                      key={p}
                      type="button"
                      data-testid={`form-process-${p}`}
                      onClick={() => toggleProcess(p)}
                      className={`px-2.5 py-1 text-[11px] font-mono border rounded-sm transition-colors ${
                        on ? "bg-primary border-primary text-white" : "border-border text-muted-foreground hover:border-primary"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>
            {[
              ["hardness", "Hardness"], ["porosity", "Porosity"], ["bond_strength", "Bond Strength"],
              ["density", "Density"], ["melting_point", "Melting Point"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</label>
                <Input data-testid={`form-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`mt-1.5 ${inputCls}`} />
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Spray Parameters</label>
              <Textarea data-testid="form-spray_parameters" value={form.spray_parameters} onChange={(e) => setForm({ ...form, spray_parameters: e.target.value })} className={`mt-1.5 min-h-[70px] resize-none ${inputCls}`} />
            </div>
            <div className="sm:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Coating Microstructure</label>
              <Textarea data-testid="form-microstructure" value={form.microstructure} onChange={(e) => setForm({ ...form, microstructure: e.target.value })} placeholder="e.g. lamellar splats, dense with fine carbide dispersion, low oxide stringers" className={`mt-1.5 min-h-[70px] resize-none ${inputCls}`} />
            </div>
            <div className="sm:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Applications</label>
              <Textarea data-testid="form-applications" value={form.applications} onChange={(e) => setForm({ ...form, applications: e.target.value })} className={`mt-1.5 min-h-[60px] resize-none ${inputCls}`} />
            </div>
            <div className="sm:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Notes</label>
              <Textarea data-testid="form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`mt-1.5 min-h-[60px] resize-none ${inputCls}`} />
            </div>
            <div className="sm:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">SEM / Cross-section Micrograph</label>
              {editId ? (
                <div className="mt-1.5 border border-border bg-background rounded-sm p-3">
                  {formMicroPath ? (
                    <div className="flex items-start gap-3">
                      <img
                        data-testid="micrograph-preview"
                        src={`${API}/materials/${editId}/image?v=${microVer}`}
                        alt="micrograph"
                        className="h-24 w-24 object-cover rounded-sm border border-border"
                      />
                      <div className="flex flex-col gap-2">
                        <span className="font-mono text-[11px] text-muted-foreground">Micrograph attached.</span>
                        <div className="flex gap-2">
                          <label className="cursor-pointer text-xs font-mono uppercase tracking-wide text-secondary hover:text-secondary/80">
                            Replace
                            <input ref={microRef} data-testid="micrograph-input" type="file" accept="image/*" className="hidden" onChange={uploadMicrograph} />
                          </label>
                          <button data-testid="micrograph-remove" onClick={removeMicrograph} className="text-xs font-mono uppercase tracking-wide text-destructive hover:text-destructive/80">Remove</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-mono text-muted-foreground hover:text-primary">
                      <UploadSimple size={16} weight="bold" />
                      {microBusy ? "Uploading..." : "Upload micrograph (png/jpg/webp, max 8 MB)"}
                      <input ref={microRef} data-testid="micrograph-input" type="file" accept="image/*" className="hidden" onChange={uploadMicrograph} />
                    </label>
                  )}
                </div>
              ) : (
                <p className="mt-1.5 font-mono text-[11px] text-muted-foreground">Save the material first, then reopen it to attach a micrograph image.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm"><X size={16} className="mr-1" /> Cancel</Button>
            <Button data-testid="save-material-btn" onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-primary/90 text-white font-mono uppercase text-xs tracking-widest">
              {saving ? "Saving..." : editId ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-white">Remove material?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              This will permanently delete <span className="text-white font-mono">{deleteTarget?.name}</span> from the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={confirmDelete} className="rounded-sm bg-destructive hover:bg-destructive/90 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
